import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { settingsSelectors } from 'store/settings'
import clsx from 'clsx'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { Spinner } from '@/components/ui/spinner'
import { InputGroup, InputGroupAddon, InputGroupButton } from '@/components/ui/input-group'
import { ComposerTextarea } from 'components/ui/ComposerTextarea'
import { QuestionChip } from 'components/ui/QuestionChip'
import { SelectablePill } from 'components/ui/SelectablePill'
import { OdinCombobox } from '@/components/ui/odin/odin-combobox'
import { getSelectableAreaOptions } from 'utils/areas'
import { useGetSetting } from 'api/settings'
import { useGetAreas } from 'api/areas'
import { useGetDefaultQuestions } from 'api/default-questions'
import { getDefaultQuestionTexts } from 'hooks/useSuggestedQuestions'
import { openNewChat } from 'api/chats'

type ChatHeroProps = {
  areaId?: string
  /** Notebook mode: the new chat is locked to this agent. */
  agentId?: string
  contextId?: string
  onPendingQuestion: (question: string) => void
  onPendingCancelled: () => void
}

export const ChatHero = ({ areaId, agentId, contextId, onPendingQuestion, onPendingCancelled }: ChatHeroProps) => {
  const { t, i18n } = useTranslation()
  const { setting, title, description } = useGetSetting()
  const { data: areas } = useGetAreas()
  const [selectedAreaId, setSelectedAreaId] = useState<string | undefined>(undefined)
  const [userInput, setUserInput] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // In the multi-area flow the server picks the areas: no picker, no areaId
  const isMultiArea = useSelector(settingsSelectors.isMultiAreaFlow)

  const areaOptions = useMemo(() => getSelectableAreaOptions(areas), [areas])
  // Any nesting -> searchable combobox; a purely flat area set keeps the simpler pills.
  const hasSubAreas = useMemo(() => (areas ?? []).some((area) => (area.children?.length ?? 0) > 0), [areas])

  const resolvedAreaId = useMemo(() => {
    if (areaId) return areaId
    if (areaOptions.length === 1) return areaOptions[0].area._id
    return selectedAreaId
  }, [areaId, areaOptions, selectedAreaId])

  const areaIds = !isMultiArea && resolvedAreaId ? [resolvedAreaId] : undefined
  const { defaultQuestions } = useGetDefaultQuestions(areaIds)

  const questions = useMemo(
    () => getDefaultQuestionTexts(defaultQuestions, i18n.language),
    [defaultQuestions, i18n.language],
  )

  const showAreaPicker = !isMultiArea && !areaId && areaOptions.length > 1

  // Preselect the first selectable area so the user can start typing right away; they can change it
  // from the dropdown.
  useEffect(() => {
    if (showAreaPicker && !selectedAreaId && areaOptions.length) {
      setSelectedAreaId(areaOptions[0].area._id)
    }
  }, [showAreaPicker, selectedAreaId, areaOptions])

  const canType = isMultiArea || !!resolvedAreaId
  const canSend = canType && userInput.trim().length > 0 && !submitting

  const submit = async (text: string) => {
    const question = text.trim()
    if (!question || submitting || (!isMultiArea && !resolvedAreaId)) return
    setSubmitting(true)
    onPendingQuestion(question)
    try {
      await openNewChat({ ...(isMultiArea ? {} : { areaId: resolvedAreaId }), agentId, contextId })
    } catch (e) {
      console.error({ e })
      onPendingCancelled()
      setSubmitting(false)
    }
  }

  return (
    <div className="scrollbar-hidden flex h-full flex-col items-center justify-center overflow-y-auto px-4 py-8">
      <div className="animate-fade-slide-in flex w-full max-w-2xl flex-col items-center text-center">
        {setting?.logo?.full && (
          <img
            src={setting?.logo?.thumbnail?.medium ?? setting?.logo?.full}
            alt="Logo"
            className="mb-5 h-32 w-32 rounded-2xl object-cover"
          />
        )}
        <h1 className="m-0 text-3xl font-bold text-foreground">{title}</h1>
        <p className="mt-2 mb-12 text-base text-muted-foreground">{description}</p>

        {showAreaPicker && (
          <div className="mb-4 flex w-full flex-col items-start gap-2">
            <span className="text-sm text-muted-foreground">{t('selectAreaHint')}</span>
            {hasSubAreas ? (
              <OdinCombobox
                options={areaOptions}
                selectedId={selectedAreaId}
                onSelect={setSelectedAreaId}
                triggerPlaceholder={t('chooseArea')}
                searchPlaceholder={t('searchArea')}
                noResultsLabel={t('noAreasFound')}
              />
            ) : (
              <div className="flex flex-wrap items-center justify-start gap-2">
                {areas?.map((area) => (
                  <SelectablePill
                    key={area._id}
                    label={area.name}
                    selected={area._id === selectedAreaId}
                    disabled={area.numberOfAreaSources === 0}
                    onClick={() => setSelectedAreaId(area._id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <InputGroup className="bg-background">
          <ComposerTextarea
            value={userInput}
            onChange={setUserInput}
            onSubmit={() => submit(userInput).catch(console.error)}
            placeholder={canType ? t('typeMessage') : t('chooseArea')}
            disabled={!canType || submitting}
            maxHeight={160}
            autoFocusWhenEnabled
            className={clsx(!canType && 'cursor-not-allowed')}
          />
          <InputGroupAddon align="block-end" className="cursor-auto p-2">
            <InputGroupButton
              type="button"
              variant="default"
              size="icon-sm"
              disabled={!canSend}
              aria-label={t('sendMessage')}
              className="ml-auto"
              onClick={() => {
                submit(userInput).catch(console.error)
              }}
            >
              {submitting ? <Spinner aria-hidden /> : <OdinIcon aria-hidden icon={['far', 'arrow-up']} />}
            </InputGroupButton>
          </InputGroupAddon>
        </InputGroup>

        {questions.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {questions.map((question, index) => (
              <QuestionChip
                key={question}
                question={question}
                disabled={!canType || submitting}
                onClick={() => submit(question).catch(console.error)}
                animationDelayMs={100 + index * 80}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
